import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BufferedEventBus } from './BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';
import { Stopwatch } from './Stopwatch';

/**
 * Fires `onElapsed` once after `ms` of *running* time. startTimer()/stopTimer()
 * resume/pause that running time, so time spent stopped does not count and the
 * remaining budget carries across start/stop cycles.
 *
 * Remaining time is recomputed from the wall clock (via Stopwatch) on start and on
 * every foreground, so a background-throttled timer corrects to the true remaining
 * time instead of drifting. The shared Stopwatch is the same primitive the recording
 * timer uses (see Stopwatch.ts / IH-6926).
 */
export function useElapsedTimer(onElapsed: () => void, ms: number) {
    const eventBus = useInstance(BufferedEventBus);

    // Running while the countdown is active; elapsedMs() is the running time consumed so far.
    const [stopwatch] = useState(() => new Stopwatch());

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const finishedRef = useRef(false);

    // Hold the latest callback so the foreground listener never needs to resubscribe.
    const onElapsedRef = useRef(onElapsed);
    onElapsedRef.current = onElapsed;

    const clearPendingTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const fire = useCallback(() => {
        finishedRef.current = true;
        clearPendingTimeout();
        onElapsedRef.current();
    }, []);

    // (Re)schedule for the time still remaining, computed from the wall clock. Safe to
    // call repeatedly (e.g. on every foreground): fires immediately if already due,
    // otherwise reschedules to the accurate remaining time.
    const schedule = useCallback(() => {
        clearPendingTimeout();
        if (finishedRef.current || !stopwatch.isRunning()) {
            return;
        }
        const remaining = ms - stopwatch.elapsedMs();
        if (remaining <= 0) {
            fire();
            return;
        }
        timeoutRef.current = setTimeout(fire, remaining);
    }, [ms, stopwatch, fire]);

    const startTimer = useCallback(() => {
        if (finishedRef.current || stopwatch.isRunning()) {
            return;
        }
        stopwatch.resume();
        schedule();
    }, [stopwatch, schedule]);

    const stopTimer = useCallback(() => {
        stopwatch.pause();
        clearPendingTimeout();
    }, [stopwatch]);

    // JS timers are throttled/suspended while backgrounded, so recompute remaining on foreground.
    useEffect(() => {
        const unsub = eventBus.on(APP_FOREGROUNDED, schedule);
        return () => {
            unsub();
            clearPendingTimeout();
        };
    }, [eventBus, schedule]);

    // Reset when the duration changes.
    useEffect(() => {
        finishedRef.current = false;
        stopwatch.reset();
        clearPendingTimeout();
    }, [ms, stopwatch]);

    return { startTimer, stopTimer };
};
